import {
  Add,
  Block,
  Delete,
  Edit,
  Timeline,
  Warning,
} from '@mui/icons-material';
import { Box, Chip, IconButton, Paper, Typography } from '@mui/material';
import {
  addToBlacklist,
  getBlacklistHistory,
  removeFromBlacklist,
  updateBlacklist,
} from 'lib/api/blackList';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import AddBlacklistDialog from './components/AddBlacklistDialog';
import DeleteBlacklistDialog from './components/DeleteBlacklistDialog';
import EditReasonDialog from './components/EditReasonDialog';
import HistoryDialog from './components/HistoryDialog';

interface BlacklistHistoryItem {
  id: number;
  businessIdentifier: string;
  businessName: string;
  action: 'agregó' | 'eliminó';
  actionDate: string;
  reason?: string;
  userId: number;
  userName: string;
}

interface BlacklistProps {
  businessId: string;
  businessName: string;
}

const Blacklist: React.FC<BlacklistProps> = (props) => {
  const { businessId, businessName } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [isInBlackList, setIsInBlackList] = useState<boolean>(false);
  const [editingReason, setEditingReason] = useState<boolean>(false);
  const [addingBlacklist, setAddingBlacklist] = useState<boolean>(false);
  const [deletingBlacklist, setDeletingBlacklist] = useState<boolean>(false);
  const [showingHistory, setShowingHistory] = useState<boolean>(false);
  const [reason, setReason] = useState<string>('');
  const [newReason, setNewReason] = useState<string>('');
  const [deleteReason, setDeleteReason] = useState<string>('');
  const [lastBlacklistItem, setLastBlacklistItem] =
    useState<BlacklistHistoryItem | null>(null);
  const [blacklistHistory, setBlacklistHistory] = useState<
    BlacklistHistoryItem[]
  >([]);
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const dispatch = useDispatch();

  const fetchBlacklistHistory = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    try {
      if (!businessId) return;
      const blacklist = await getBlacklistHistory(
        dispatch,
        `page=${page}&pageSize=${pageSize}&businessIdentifier=${businessId}`,
      );
      setPagination(blacklist.pagination);
      setBlacklistHistory(blacklist.data);
      return blacklist;
    } catch (error) {
      console.error('Error fetching blacklist history:', error);
    }
  };

  const triggerSearch = async () => {
    const response = await fetchBlacklistHistory({
      page: 1,
      pageSize: PAGE_SIZE,
    });
    const firstItem = response.data[0];
    if (firstItem) {
      setIsInBlackList(firstItem.action === 'add');
      setLastBlacklistItem(firstItem);
      setReason(firstItem.reason || '');
      setNewReason(firstItem.reason || '');
    } else {
      setIsInBlackList(false);
      setReason('');
    }
  };

  useEffect(() => {
    void triggerSearch();
  }, []);

  const handleUpdateBlacklist = async () => {
    if (!newReason) return;
    setLoading(true);
    try {
      await updateBlacklist(dispatch, {
        id: lastBlacklistItem?.id,
        reason: newReason,
      });
      await triggerSearch();
      showAlert(dispatch, 'success', 'Razón actualizada');
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    } finally {
      setLoading(false);
      setEditingReason(false);
    }
  };

  const handleDeleteBlacklist = async () => {
    setLoading(true);
    try {
      await removeFromBlacklist(dispatch, businessId, { reason: deleteReason });
      await triggerSearch();
      showAlert(dispatch, 'success', 'Empresa eliminada de la blacklist');
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    } finally {
      setLoading(false);
      setDeletingBlacklist(false);
    }
  };

  const handleAddBlacklist = async () => {
    if (!reason) return;
    setLoading(true);
    try {
      await addToBlacklist(dispatch, businessId, { reason, businessName });
      await triggerSearch();
      showAlert(dispatch, 'success', 'Empresa agregada a la blacklist');
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    } finally {
      setLoading(false);
      setAddingBlacklist(false);
    }
  };

  if (loading) return <CenteredCircularProgress relative />;

  return (
    <Paper
      sx={{
        p: 3,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        position: 'relative',
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1.2rem',
              lineHeight: 1.1,
            }}
          >
            Blacklist
          </Typography>
          <Chip
            label={isInBlackList ? 'En Blacklist' : 'Sin Restricciones'}
            size="small"
            color={isInBlackList ? 'error' : 'success'}
            variant="outlined"
            sx={{ height: '22px', fontSize: '0.75rem' }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => setShowingHistory(true)}
            sx={{
              padding: 1,
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.2)',
              },
            }}
          >
            <Timeline sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
          </IconButton>
          {isInBlackList && (
            <>
              <IconButton
                size="small"
                onClick={() => setEditingReason(true)}
                sx={{
                  padding: 1,
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 152, 0, 0.2)',
                  },
                }}
              >
                <Edit sx={{ fontSize: '1.1rem', color: 'warning.main' }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setDeletingBlacklist(true)}
                sx={{
                  padding: 1,
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 0.2)',
                  },
                }}
              >
                <Delete sx={{ fontSize: '1.1rem', color: 'error.main' }} />
              </IconButton>
            </>
          )}
          {!isInBlackList && (
            <IconButton
              size="small"
              onClick={() => setAddingBlacklist(true)}
              sx={{
                padding: 1,
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.2)',
                },
              }}
            >
              <Add sx={{ fontSize: '1.1rem', color: 'error.main' }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box
            sx={{
              backgroundColor: isInBlackList
                ? 'rgba(244, 67, 54, 0.1)'
                : 'rgba(76, 175, 80, 0.1)',
              borderRadius: '50%',
              p: 0.4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '26px',
              height: '26px',
              mt: 0.5,
              flexShrink: 0,
            }}
          >
            {isInBlackList ? (
              <Block sx={{ fontSize: '0.9rem', color: 'error.main' }} />
            ) : (
              <Warning sx={{ fontSize: '0.9rem', color: 'success.main' }} />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}
            >
              Estado
            </Typography>
            {isInBlackList ? (
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.85rem',
                    lineHeight: 1.4,
                    color: 'error.main',
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  La empresa está en la blacklist
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}
                >
                  Razón
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.85rem',
                    lineHeight: 1.4,
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {reason}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                  color: 'success.main',
                  fontWeight: 600,
                }}
              >
                La empresa no está en la blacklist
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <AddBlacklistDialog
        open={addingBlacklist}
        onClose={() => setAddingBlacklist(false)}
        businessName={businessName}
        reason={reason}
        onReasonChange={setReason}
        onAdd={handleAddBlacklist}
        loading={loading}
      />

      <EditReasonDialog
        open={editingReason}
        onClose={() => setEditingReason(false)}
        businessName={businessName}
        newReason={newReason}
        onReasonChange={setNewReason}
        onSave={handleUpdateBlacklist}
        loading={loading}
      />

      <DeleteBlacklistDialog
        open={deletingBlacklist}
        onClose={() => setDeletingBlacklist(false)}
        businessName={businessName}
        deleteReason={deleteReason}
        onReasonChange={setDeleteReason}
        onDelete={handleDeleteBlacklist}
        loading={loading}
        reason={reason}
      />

      <HistoryDialog
        open={showingHistory}
        onClose={() => setShowingHistory(false)}
        businessId={businessId}
        businessName={businessName}
        fetchBlacklistHistory={fetchBlacklistHistory}
      />
    </Paper>
  );
};

export default Blacklist;
